// CoinLogo.jsx
import React, { useMemo, useState, lazy, Suspense } from 'react';
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CandlestickChartIcon from '@mui/icons-material/ShowChart';

// Lazy-load the heavy TradingView widget so lists stay fast
const TradingViewWidget = lazy(() => import('./TradingViewWidget'));

const CoinLogo = ({ coinPair }) => {
  const [imgError, setImgError] = useState(false);
  const [open, setOpen] = useState(false);

  // Derive symbols once
  const { baseCoin, imageUrl } = useMemo(() => {
    const base = (coinPair || '').split('-')[0]?.toUpperCase() || '';
    return {
      baseCoin: base,
      imageUrl: `https://cryptomarketdarts.com/api/img/coins/${base}.png`,
    };
  }, [coinPair]);

  if (!baseCoin) return null;

  return (
    <>
      {/* Inline: logo + tiny icon button */}
      {!imgError && (
        <img
          src={imageUrl}
          alt={`${baseCoin} logo`}
          width={20}
          height={20}
          style={{ marginRight: 6, verticalAlign: 'middle' }}
          onError={() => setImgError(true)}
        />
      )}

      <Tooltip title="Open chart" arrow>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation(); // don’t trigger row clicks
            setOpen(true);
          }}
          sx={{ padding: '2px', marginLeft: '2px' }}
        >
          <CandlestickChartIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      {/* Modal with full-width chart (mounted only when open) */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            width: '92vw',
            height: '86vh',
            m: 0,
            backgroundColor: 'background.default',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          {baseCoin} / USD
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: 'calc(100% - 56px)' }}>
          <Suspense fallback={<div style={{ padding: 16 }}>Loading chart…</div>}>
            {/* Pass both coinPair and baseCoin to the widget; give it full height */}
            <TradingViewWidget
              coin={coinPair}
              leftSideName={baseCoin}
              height="100%"
            />
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(CoinLogo);
